/**
 * Reports — the legacy's issue roll-up, rebuilt (ADR-0021 flow parity).
 *
 * The legacy closes its flow with a tab answering one question: what is wrong
 * with this month, and how bad is it? Practitioners use it as the pre-filing
 * sweep.
 *
 * Ours reads the same computation the gates panel and the prepare button run
 * (ADR-0014) — nothing here is a second opinion. The severity split is the
 * honest one:
 *
 * - Error — it blocks `prepared`: Table-12 shortfalls, tie-out breaks.
 * - Warning — it does not block, but it changes the return: rows held out of
 *   the filing until fixed, and rows awaiting a V-gated verification.
 * - For information — documents still held in intake. They are not in this
 *   return at all yet, which is worth seeing before filing it.
 *
 * The legacy also carries an "Auto-fixed" bucket. We deliberately have no
 * equivalent: nothing in ProGST edits a row on its own — a suggestion waits
 * for a person to accept it (D-16). A bucket claiming otherwise would be a
 * comforting lie.
 */

import { Alert, Button } from '@fiveox/ui'
import { ClipboardList } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  type DocumentRecord,
  type PeriodGates,
  type PeriodRecord,
  documents,
  periods,
} from '../lib/api'
import { rowWhere, rowWhy } from '../lib/gateRows'
import { useClient } from './ClientLayout'

type Severity = 'error' | 'warning' | 'info'

/** Issues per page — the table grows by this much on "Show more". */
const ISSUE_PAGE = 100

interface Issue {
  severity: Severity
  category: string
  detail: string
  row?: string
}

const SEVERITY_LABEL: Record<Severity, string> = {
  error: 'Errors',
  warning: 'Warnings',
  info: 'For information',
}

const CHIP: Record<Severity, string> = {
  error: 'is-stop',
  warning: 'is-hold',
  info: 'is-ok',
}

export function Reports() {
  const client = useClient()
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [gates, setGates] = useState<PeriodGates | null>(null)
  const [held, setHeld] = useState<DocumentRecord[]>([])
  const [filter, setFilter] = useState<Severity | 'all'>('all')
  // A page at a time: one real month carried 5,891 Table 12 issues in a single table.
  const [limit, setLimit] = useState(ISSUE_PAGE)
  useEffect(() => setLimit(ISSUE_PAGE), [filter, selected])
  const [error, setError] = useState<string | null>(null)

  // **This client's periods only.** Listing them with no client listed every client's in the
  // org, so a period of client B could be selected here and shown beside client A's held
  // documents — right numbers, wrong client (N1, 2026-09-29).
  useEffect(() => {
    void periods
      .list({ client_id: client.id })
      .then((b) => {
        setRows(b.data)
        setSelected((cur) => cur ?? b.data[0]?.id ?? null)
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.'),
      )
  }, [client.id])

  const load = useCallback(async () => {
    if (!selected) {
      setGates(null)
      return
    }
    try {
      const [g, d] = await Promise.all([
        periods.gates(selected),
        documents.list({ client_id: client.id }),
      ])
      setGates(g.data)
      setHeld(d.data.filter((x) => x.held && x.held !== 'superseded'))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The report could not be built.')
    }
  }, [client.id, selected])

  useEffect(() => {
    void load()
  }, [load])

  const issues: Issue[] = gates
    ? [
        // Named by invoice and said in words — `lib/gateRows`, shared with the gates panel. This
        // printed the check code and twelve characters of a content hash (2026-09-29).
        ...gates.blocked_table12.map((b) => ({
          severity: 'error' as const,
          category: 'Table 12',
          detail: `${rowWhy(b)} — blocks prepared`,
          row: rowWhere(b),
        })),
        ...(gates.marked_table12 ?? []).map((m) => ({
          severity: 'warning' as const,
          category: 'Table 12, marked',
          detail: rowWhy(m),
          row: rowWhere(m),
        })),
        ...gates.tieout.map((t) => ({
          severity: 'error' as const,
          category: 'Tie-out',
          detail: String(t.message),
        })),
        ...gates.excluded.map((e) => ({
          severity: 'warning' as const,
          // Same distinction the gates panel and the client workbook make: a
          // row a person removed is not a row a check held, and `failing` is
          // empty for the first, so the old line was a bare "— joins on fix".
          category:
            e.excluded_by === 'reviewer' ? 'Removed by the preparer' : 'Held out of the return',
          detail:
            e.excluded_by === 'reviewer' ? rowWhy(e) : `${rowWhy(e)} — joins the return once fixed`,
          row: rowWhere(e),
        })),
        ...gates.pending.map((p) => ({
          severity: 'warning' as const,
          category: 'Awaiting verification',
          detail: rowWhy(p),
          row: rowWhere(p),
        })),
        ...held.map((h) => ({
          severity: 'info' as const,
          category: 'Held in intake',
          detail: `${h.source_filename} — ${h.next_action ?? h.held}`,
        })),
      ]
    : []

  const count = (s: Severity) => issues.filter((i) => i.severity === s).length
  const inBucket = filter === 'all' ? issues : issues.filter((i) => i.severity === filter)
  const shown = inBucket.slice(0, limit)

  return (
    <div>
      <header className="mb-6">
        <h1 className="gst-h1">
          <ClipboardList size={20} /> Reports
        </h1>
        <p className="gst-sub">
          What stands between this month and a filing — the same computation the gates run
        </p>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      <div className="flex flex-wrap gap-6">
        <aside className="w-72 shrink-0">
          {rows.length === 0 ? (
            <div className="gst-empty">No periods yet.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="truncate">{row.return_type}</span>
                    <span className="mt-0.5 block text-[11px]">{row.period}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="min-w-0 flex-1">
          {!gates ? (
            <p className="gst-sub">Select a period.</p>
          ) : (
            <>
              {/* The legacy's cards are the filter — click one, see only those. */}
              <div className="gst-stats mb-4">
                <button
                  type="button"
                  className="gst-stat"
                  onClick={() => setFilter('all')}
                  aria-pressed={filter === 'all'}
                >
                  <div className="n">{issues.length}</div>
                  <div className="l">TOTAL ISSUES</div>
                </button>
                {(['error', 'warning', 'info'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="gst-stat"
                    onClick={() => setFilter(s)}
                    aria-pressed={filter === s}
                  >
                    <div className={`n ${s === 'error' ? 'is-tax' : ''}`}>{count(s)}</div>
                    <div className="l">{SEVERITY_LABEL[s].toUpperCase()}</div>
                  </button>
                ))}
              </div>

              {shown.length === 0 ? (
                <div className="gst-empty">
                  {issues.length === 0
                    ? 'Nothing to report. This month is clean.'
                    : 'Nothing in this bucket.'}
                </div>
              ) : (
                <div className="gst-table-wrap">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Severity</th>
                        <th className="is-text">Category</th>
                        <th className="is-text">Detail</th>
                        <th className="is-text">Where</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((issue, i) => (
                        <tr key={i} className={i % 2 === 1 ? 'is-a' : ''}>
                          <td className="is-text">
                            <span className={`gst-chip ${CHIP[issue.severity]}`}>
                              {issue.severity.toUpperCase()}
                            </span>
                          </td>
                          <td className="is-text is-sec">{issue.category}</td>
                          <td className="is-text">{issue.detail}</td>
                          <td className="is-text">
                            {issue.row ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {inBucket.length > shown.length && (
                    <Button type="button" variant="outline" size="sm" className="m-3"
                      onClick={() => setLimit((n) => n + ISSUE_PAGE)}>
                      Show {Math.min(ISSUE_PAGE, inBucket.length - shown.length).toLocaleString('en-IN')}{' '}
                      more of {(inBucket.length - shown.length).toLocaleString('en-IN')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}
